import React from "react";
import { siteConfig } from "../../config/siteConfig";
import Icon from "../ui/Icon";
import SealChip from "../ui/SealChip";
import Reveal from "../ui/Reveal";
import Eyebrow from "../ui/Eyebrow";

const Pricing = ({ onRegisterClick }) => {
  const { pricing } = siteConfig;
  const plan = pricing.plan;

  // Réassurance dockée sous le CTA (jamais faded-on-hover). Fallback sûr si absent.
  const reassurance = Array.isArray(pricing.reassurance) ? pricing.reassurance : [];

  // Anchor price barré : rendu uniquement si présent (DESIGN-SPEC §4 Pricing).
  const anchorPrice = pricing.anchorPrice;

  return (
    <section
      id="pricing"
      className="relative scroll-mt-24 py-28 md:py-36 bg-mesh overflow-hidden"
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div
          className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-teal-100 blur-[120px] opacity-50"
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] rounded-full bg-cyan-50 blur-[120px] opacity-50"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-14">
          <Eyebrow as="div" className="justify-center">
            {pricing.eyebrow || "Precio único"}
          </Eyebrow>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-balance text-slate-900 mt-4 mb-5">
            {pricing.eyebrowTitle1} <br />
            <span className="text-teal-700">{pricing.eyebrowTitle2}</span>
          </h2>
          <p className="text-lg md:text-xl leading-snug text-pretty text-slate-600">
            {pricing.eyebrowSubtitle}
          </p>
        </Reveal>

        <Reveal as="div" index={1} className="max-w-md mx-auto">
          {/* PRICING-HERO : gradient-border aurora + glow réservé + élévation modale */}
          <div className="relative aurora-ring rounded-2xl glow-cta elev-3">
            <div className="relative rounded-2xl bg-white p-8 md:p-10">
              {/* Ruban « más popular » */}
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-to-r from-teal-700 to-cyan-600 text-white text-[11px] font-semibold uppercase tracking-wider shadow-md whitespace-nowrap">
                {pricing.badgeLabel || "Más popular"}
              </span>

              <div className="mb-7 flex flex-col items-start gap-3">
                <SealChip label={pricing.sealLabel || "Formato del examen oficial"} icon="seal" />
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                  <p className="text-slate-500 text-sm mt-1">{plan.description}</p>
                </div>
              </div>

              <div className="mb-9">
                {anchorPrice ? (
                  <p className="font-display tabular-nums text-xl text-slate-400 line-through mb-1">
                    {plan.currency}
                    {anchorPrice}
                  </p>
                ) : null}
                <div className="flex items-baseline gap-1">
                  <span className="font-display tabular-nums text-6xl md:text-7xl font-extrabold text-slate-900 leading-none">
                    {plan.currency}
                    {plan.price}
                  </span>
                  <span className="text-lg font-display tabular-nums text-slate-400">
                    {plan.period}
                  </span>
                </div>
                {plan.priceSubtext ? (
                  <p className="text-sm text-slate-500 mt-2">{plan.priceSubtext}</p>
                ) : null}
              </div>

              <div className="space-y-3.5 mb-9">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Icon
                      name="check"
                      className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5"
                    />
                    <span className="text-slate-700 font-medium text-sm leading-snug">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={onRegisterClick}
                aria-label={plan.ctaLabel}
                className="btn-primary w-full py-4 text-lg"
              >
                {plan.ctaLabel}
              </button>

              {/* Chips de réassurance DOCKÉS sous le CTA — jamais faded-on-hover */}
              {reassurance.length > 0 ? (
                <ul className="mt-5 flex flex-col gap-2">
                  {reassurance.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-2.5 text-sm font-medium text-slate-600"
                    >
                      <Icon
                        name={item.iconKey || "check"}
                        className="w-4 h-4 text-teal-700 flex-shrink-0"
                      />
                      <span>{item.label}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-5 text-center text-xs font-medium text-slate-400">
                  {pricing.guaranteeNote ||
                    "Sin permanencia · cancela cuando quieras"}
                </p>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal
          as="div"
          index={2}
          className="relative z-10 mt-12 flex flex-col items-center gap-4"
        >
          <div className="flex flex-col items-center gap-1.5 text-center">
            <div className="flex items-center gap-2 text-teal-800">
              <Icon name="lock" className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-widest">
                {pricing.securityCopy?.label || "Pago 100% seguro"}
              </span>
            </div>
            <p className="text-slate-400 text-xs max-w-xs leading-relaxed">
              {pricing.securityCopy?.text ||
                "Tus datos están protegidos por encriptación SSL de 256 bits. No almacenamos los datos de tu tarjeta."}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
